import React, { useEffect, useRef, useState } from 'react';
import {
  Select, Button, Modal, Form, DatePicker, Progress, Tag, Collapse,
  Input, App, Spin, Empty, Avatar, Upload, Divider,
} from 'antd';
import {
  LockOutlined, CheckCircleOutlined, ClockCircleOutlined,
  UploadOutlined, UserOutlined, ThunderboltOutlined, EyeOutlined, EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import AIGeneratorPanel from '../components/AIGeneratorPanel';
import MathRenderer from '../components/MathRenderer';
import StagePipeline, { STATUS_CHEVRON } from '../components/StagePipeline';
import dayjs from 'dayjs';

function calcReadiness(rows) {
  const countable = rows.filter(p => p.stage_status !== 'skipped');
  const done = countable.filter(p => p.stage_status === 'completed');
  return countable.length ? Math.round((done.length / countable.length) * 100) : 0;
}

// ─────────────────────────────────────────────────────────────────────────────
function TutorView({ user }) {
  const [searchParams] = useSearchParams();
  const [students, setStudents]         = useState([]);
  const [selStudent, setSelStudent]     = useState(null);
  const [allAssignments, setAllAssignments] = useState([]);
  const [templates, setTemplates]       = useState([]);
  const [selAssignment, setSelAssignment] = useState(null);
  const [progress, setProgress]         = useState([]);
  const [selStage, setSelStage]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [progLoading, setProgLoading]   = useState(false);
  const [assignModal, setAssignModal]   = useState(false);
  const [reviewNotes, setReviewNotes]     = useState('');
  const [rejectionFeedback, setRejectionFeedback] = useState('');
  const [tmplStages, setTmplStages]     = useState([]);
  const [aiOpen, setAiOpen]             = useState(false);
  const [previewOpen, setPreviewOpen]   = useState(false);
  const [examModal, setExamModal]       = useState(null);  // holds the assignment being edited
  const [examDate, setExamDate]         = useState(null);
  const [examSaving, setExamSaving]     = useState(false);
  const [assignForm] = Form.useForm();
  const { message } = App.useApp();
  const pollRef = useRef(null);

  const assignments = selStudent
    ? allAssignments.filter(a => a.student_id === selStudent)
    : [];

  const loadAll = async () => {
    try {
      const [sRes, tRes, aRes] = await Promise.all([
        api.get('/api/gate/students'),
        api.get('/api/gate/templates/mine'),
        api.get(`/api/gate/assignments/tutor/${user.userId}`),
      ]);
      setStudents(sRes.data);
      setTemplates(tRes.data);
      setAllAssignments(aRes.data);
      return { students: sRes.data, assignments: aRes.data };
    } catch { message.error('Failed to load'); return {}; }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadAll().then(({ students: freshStudents, assignments: freshAssignments }) => {
      const paramId = Number(searchParams.get('studentId'));
      if (!paramId || !freshAssignments) return;
      setSelStudent(paramId);
      const first = freshAssignments.find(a => a.student_id === paramId);
      if (first) loadProgress(first);
    });
  }, []);

  // Poll progress every 15s when an assignment is open
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!selAssignment) return;
    pollRef.current = setInterval(() => {
      api.get(`/api/gate/assignments/${selAssignment.assignment_id}/progress`)
        .then(r => setProgress(r.data))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(pollRef.current);
  }, [selAssignment?.assignment_id]);

  const loadStudentAssignments = async (studentId) => {
    setSelStudent(studentId);
    setSelAssignment(null);
    setProgress([]);
    setSelStage(null);
    const first = allAssignments.find(a => a.student_id === studentId);
    if (first) loadProgress(first);
  };

  const loadProgress = async (assignment) => {
    setSelAssignment(assignment);
    setSelStage(null);
    setProgLoading(true);
    try {
      const res = await api.get(`/api/gate/assignments/${assignment.assignment_id}/progress`);
      setProgress(res.data);
    } catch { message.error('Failed to load progress'); }
    finally { setProgLoading(false); }
  };

  const handleReview = async (progressId, decision) => {
    if (decision === 'approved' && !reviewNotes.trim()) {
      message.warning('Approval notes are required before approving.');
      return;
    }
    if (decision === 'rejected' && !rejectionFeedback.trim()) {
      message.warning('Rejection feedback is required before sending back.');
      return;
    }
    try {
      await api.patch(`/api/gate/progress/${progressId}/review`, {
        decision,
        tutor_notes: reviewNotes,
        tutor_feedback: decision === 'rejected' ? rejectionFeedback : undefined,
      });
      message.success(decision === 'approved' ? 'Stage approved — next stage unlocked' : 'Stage sent back for revision');
      setReviewNotes('');
      setRejectionFeedback('');
      setSelStage(null);
      loadProgress(selAssignment);
    } catch (e) { message.error(e.message); }
  };

  const handleUnlock = async (progressId) => {
    try {
      await api.patch(`/api/gate/progress/${progressId}/unlock`);
      message.success('Stage unlocked');
      setSelStage(null);
      loadProgress(selAssignment);
    } catch (e) { message.error(e.message); }
  };

  const handleLock = async (progressId) => {
    try {
      await api.patch(`/api/gate/progress/${progressId}/lock`);
      message.success('Stage locked');
      setSelStage(null);
      loadProgress(selAssignment);
    } catch (e) { message.error(e.message); }
  };

  const handleSkip = async (progressId) => {
    try {
      await api.patch(`/api/gate/progress/${progressId}/skip`);
      message.success('Stage skipped');
      setSelStage(null);
      loadProgress(selAssignment);
    } catch (e) { message.error(e.message); }
  };

  const handleSync = async (assignment, e) => {
    e?.stopPropagation();
    try {
      const res = await api.post(`/api/gate/assignments/${assignment.assignment_id}/sync`);
      const added = res.data?.added || 0;
      message.success(added > 0
        ? `Synced — ${added} new stage${added !== 1 ? 's' : ''} added`
        : 'Already up to date with the template');
      loadProgress(assignment);
    } catch { message.error('Sync failed'); }
  };

  const onTmplChange = async (tmplId) => {
    assignForm.setFieldsValue({ start_stage_id: undefined });
    if (!tmplId) { setTmplStages([]); return; }
    try {
      const res = await api.get(`/api/gate/templates/${tmplId}/stages`);
      setTmplStages(res.data);
    } catch {}
  };

  const openExamModal = (assignment) => {
    setExamModal(assignment);
    setExamDate(assignment.exam_date ? dayjs(assignment.exam_date) : null);
  };

  const handleSaveExamDate = async () => {
    if (!examModal) return;
    setExamSaving(true);
    try {
      await api.patch(`/api/gate/assignments/${examModal.assignment_id}/exam-date`, {
        exam_date: examDate ? examDate.format('YYYY-MM-DD') : null,
      });
      message.success(examDate ? 'Exam date updated' : 'Exam date cleared');
      setExamModal(null);
      setExamDate(null);
      const aRes = await api.get(`/api/gate/assignments/tutor/${user.userId}`);
      setAllAssignments(aRes.data);
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to update exam date');
    } finally { setExamSaving(false); }
  };

  const handleAssign = async (values) => {
    try {
      await api.post('/api/gate/assignments', {
        student_id:     selStudent,
        template_id:    values.template_id,
        start_stage_id: values.start_stage_id || null,
        exam_date:      values.exam_date ? values.exam_date.format('YYYY-MM-DD') : null,
      });
      message.success('Template assigned');
      setAssignModal(false);
      assignForm.resetFields();
      setTmplStages([]);
      const aRes = await api.get(`/api/gate/assignments/tutor/${user.userId}`);
      setAllAssignments(aRes.data);
    } catch (e) {
      message.error(e.message || 'Failed to assign');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

  return (
    <div>
      {/* Student selector */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <Select
          style={{ width: 280 }}
          placeholder="Select a student"
          onChange={loadStudentAssignments}
          value={selStudent || undefined}
          options={Array.from(new Map(students.map(s => [s.user_id, s])).values())
            .map(s => ({ value: s.user_id, label: s.name }))}
        />
        {selStudent && (
          <Button type="primary" style={{ background: '#0a0a0a', border: 'none' }}
            onClick={() => setAssignModal(true)}>
            Assign template
          </Button>
        )}
      </div>

      {selStudent && assignments.length === 0 && !loading && (
        <Empty description="No assignments yet for this student." style={{ padding: 60 }} />
      )}

      {assignments.map(a => {
        const prog   = selAssignment?.assignment_id === a.assignment_id ? progress : [];
        const ready  = calcReadiness(prog);
        const cleared = prog.filter(p => p.stage_status === 'completed').length;
        const total   = prog.filter(p => p.stage_status !== 'skipped').length;
        const daysLeft = a.exam_date ? dayjs(a.exam_date).diff(dayjs(), 'day') : null;

        return (
          <div key={a.assignment_id} style={{ marginBottom: 24 }}>
            <div
              onClick={() => {
                if (selAssignment?.assignment_id === a.assignment_id) {
                  setSelAssignment(null); setProgress([]); setSelStage(null);
                } else {
                  loadProgress(a);
                }
              }}
              style={{
                border: `1px solid ${selAssignment?.assignment_id === a.assignment_id ? '#0a0a0a' : '#e8e8e8'}`,
                borderRadius: 10, padding: '16px 20px', cursor: 'pointer',
                background: '#fff', marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0a0a0a' }}>{a.template_title}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {a.subject_display || '—'}
                    {a.exam_date
                      ? <> · Exam: {dayjs(a.exam_date).format('D MMM YYYY')} <Button
                          type="text" size="small" icon={<EditOutlined />}
                          onClick={e => { e.stopPropagation(); openExamModal(a); }}
                          style={{ padding: '0 4px', height: 18, color: '#bbb' }}
                          title="Change exam date" /></>
                      : <> · <Button
                          type="link" size="small"
                          onClick={e => { e.stopPropagation(); openExamModal(a); }}
                          style={{ padding: 0, height: 18, fontSize: 12 }}>
                          + Set exam date
                        </Button></>
                    }
                    {a.pending_review > 0 && <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>{a.pending_review} awaiting review</Tag>}
                  </div>
                </div>
                <Progress type="circle" percent={Math.round((a.completed_stages / Math.max(a.total_stages, 1)) * 100)}
                  size={52} strokeColor="#0a0a0a" />
              </div>
            </div>

            {selAssignment?.assignment_id === a.assignment_id && (
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, padding: 16, background: '#fff' }}>
                {progLoading
                  ? <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
                  : (
                    <>
                      {/* Toolbar */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                        <Button size="small" icon={<ReloadOutlined />} onClick={(e) => handleSync(a, e)}
                          title="Pull in stages added to the template since this roadmap was assigned">
                          Sync with template
                        </Button>
                      </div>
                      {/* Pipeline */}
                      <div style={{ marginBottom: 16 }}>
                        <StagePipeline stages={progress} selected={selStage} onSelect={setSelStage} clickable />
                      </div>

                      {/* Stage detail panel */}
                      {selStage && (
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, background: '#fafafa' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                              {selStage.stage_name}
                              {selStage.stage_archived && (
                                <Tag color="default" style={{ marginLeft: 8, fontSize: 10, color: '#999' }}>
                                  Removed from template
                                </Tag>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => setPreviewOpen(true)}
                              >
                                Student view
                              </Button>
                              <Button
                                size="small"
                                icon={<ThunderboltOutlined />}
                                style={{ borderColor: '#fa8c16', color: '#fa8c16', flexShrink: 0 }}
                                onClick={() => setAiOpen(true)}
                              >
                                Generate questions
                              </Button>
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                            Gate description: <MathRenderer content={selStage.gate_description || '—'} />
                          </div>

                          {selStage.stage_status === 'submitted' && (
                            <>
                              {/* AI grade summary for tutor */}
                              {selStage.ai_grade && (
                                <div style={{ marginBottom: 12 }}>
                                  <AiGradeCard grade={selStage.ai_grade} score={selStage.ai_score} feedback={selStage.ai_feedback} />
                                </div>
                              )}
                              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Student answers</div>
                              <div style={{ marginBottom: 8 }}>
                                <StudentAnswerDisplay answer={selStage.student_answer} />
                              </div>
                              {selStage.answer_image_url && (
                                <a href={selStage.answer_image_url} target="_blank" rel="noreferrer">
                                  <img src={selStage.answer_image_url} alt="submission"
                                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, marginBottom: 8 }} />
                                </a>
                              )}
                              <Input.TextArea
                                autoSize={{ minRows: 2 }} placeholder="Approval notes * (required to approve)"
                                value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                                style={{ marginBottom: 8, borderColor: reviewNotes.trim() ? undefined : '#d9d9d9' }}
                              />
                              <Input.TextArea
                                autoSize={{ minRows: 2 }} placeholder="Rejection feedback * (required to send back)"
                                value={rejectionFeedback} onChange={e => setRejectionFeedback(e.target.value)}
                                style={{ marginBottom: 10, borderColor: rejectionFeedback.trim() ? undefined : '#d9d9d9' }}
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button type="primary" style={{ background: '#52c41a', border: 'none', flex: 1 }}
                                  onClick={() => handleReview(selStage.progress_id, 'approved')}>
                                  Approve
                                </Button>
                                <Button danger style={{ flex: 1 }}
                                  onClick={() => handleReview(selStage.progress_id, 'rejected')}>
                                  Send back for revision
                                </Button>
                              </div>
                            </>
                          )}

                          {selStage.stage_status === 'completed' && (
                            <div>
                              <div style={{ fontSize: 13, color: '#52c41a', marginBottom: 12 }}>
                                <CheckCircleOutlined /> Approved
                              </div>
                              {selStage.student_answer && (
                                <div style={{ marginBottom: 12 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Student answers</div>
                                  <StudentAnswerDisplay answer={selStage.student_answer} />
                                </div>
                              )}
                              {selStage.ai_grade && (
                                <div style={{ marginBottom: 12 }}>
                                  <AiGradeCard grade={selStage.ai_grade} score={selStage.ai_score} feedback={selStage.ai_feedback} />
                                </div>
                              )}
                              {selStage.tutor_notes && (
                                <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, fontSize: 13 }}>
                                  <span style={{ fontWeight: 600 }}>Tutor notes: </span>{selStage.tutor_notes}
                                </div>
                              )}
                            </div>
                          )}

                          {selStage.stage_status === 'active' && (
                            <div>
                              <div style={{ color: '#888', fontSize: 13, marginBottom: 10 }}>Awaiting student submission.</div>
                              <Button size="small" danger onClick={() => handleLock(selStage.progress_id)}>
                                Lock stage
                              </Button>
                            </div>
                          )}

                          {selStage.stage_status === 'locked' && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                              <Button size="small" onClick={() => handleUnlock(selStage.progress_id)}>Unlock</Button>
                              <Button size="small" danger onClick={() => handleSkip(selStage.progress_id)}>Skip</Button>
                            </div>
                          )}

                          {selStage.stage_status === 'skipped' && (
                            <div>
                              <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>This stage was skipped.</div>
                              <Button size="small" onClick={() => handleUnlock(selStage.progress_id)}>
                                Unlock (un-skip)
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )
                }
              </div>
            )}
          </div>
        );
      })}

      {/* Student view preview modal */}
      <Modal
        title={<span>Student view — <strong>{selStage?.stage_name}</strong></span>}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={620}
      >
        {selStage && (
          <div style={{ paddingTop: 8 }}>
            {selStage.gate_description && (
              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#444' }}>
                <span style={{ fontWeight: 600 }}>Gate description: </span>
                <MathRenderer content={selStage.gate_description} />
              </div>
            )}
            {selStage.questions?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Students will answer each question below:</div>
                {selStage.questions.map((q, qi) => (
                  <div key={q.question_id} style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0a0a0a', flexShrink: 0 }}>{qi + 1}.</span>
                      <QuestionBody text={q.question_text} imageUrl={q.image_url} idx={qi} />
                    </div>
                    <Input.TextArea
                      autoSize={{ minRows: 3 }}
                      placeholder="Student types answer here…"
                      disabled
                      style={{ border: 'none', borderRadius: 0, boxShadow: 'none', background: '#fff', color: '#bbb' }}
                    />
                  </div>
                ))}
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4, textAlign: 'center' }}>
                  Students can also choose to submit one photo instead of typing.
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>Students will type a free-form answer or upload an image:</div>
                <Input.TextArea autoSize={{ minRows: 4 }} placeholder="Student types answer here…" disabled style={{ background: '#fff', color: '#bbb' }} />
              </div>
            )}
          </div>
        )}
      </Modal>

      <AIGeneratorPanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        subject={selAssignment?.subject_display || ''}
        topic={selStage?.stage_name || ''}
        onSave={null}
      />

      {/* Edit exam date modal */}
      <Modal
        title={examModal ? `Exam date — ${examModal.template_title}` : 'Exam date'}
        open={!!examModal}
        maskClosable={false}
        onCancel={() => { setExamModal(null); setExamDate(null); }}
        onOk={handleSaveExamDate}
        confirmLoading={examSaving}
        okText="Save"
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            When is the student's exam? Leave blank to clear.
          </div>
          <DatePicker
            style={{ width: '100%' }}
            value={examDate}
            onChange={setExamDate}
            allowClear
            disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
          />
        </div>
      </Modal>

      {/* Assign template modal */}
      <Modal title={(() => {
        const s = students.find(s => s.user_id === selStudent);
        return s?.subject_name ? `Assign template — ${s.subject_name} ${s.level}` : 'Assign template';
      })()} open={assignModal} maskClosable={false}
        onCancel={() => { setAssignModal(false); assignForm.resetFields(); setTmplStages([]); }}
        footer={null}>
        <Form form={assignForm} layout="vertical" onFinish={handleAssign} style={{ marginTop: 16 }} requiredMark={false}>
          <Form.Item label="Template" name="template_id" rules={[{ required: true }]}>
            <Select placeholder="Pick a template" onChange={onTmplChange}
              options={(() => {
                const subjectIds = new Set(
                  students.filter(s => s.user_id === selStudent && s.tutor_subject_id)
                          .map(s => s.tutor_subject_id)
                );
                const filtered = subjectIds.size > 0
                  ? templates.filter(t => subjectIds.has(t.tutor_subject_id))
                  : templates;
                return filtered.map(t => ({ value: t.template_id, label: t.title }));
              })()} />
          </Form.Item>
          <Form.Item label="Start from stage" name="start_stage_id">
            <Select allowClear placeholder="Stage 1 (default)"
              options={tmplStages.map(s => ({ value: s.stage_id, label: `${s.stage_name}` }))} />
          </Form.Item>
          <Form.Item label="Exam date (optional)" name="exam_date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block
            style={{ background: '#0a0a0a', border: 'none' }}>
            Assign
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

// ── AI grade result card ──────────────────────────────────────────────────────
function AiGradeCard({ grade, score, feedback }) {
  const pass = grade === 'pass';
  const VERDICT_COLOR = { correct: '#52c41a', partial: '#fa8c16', incorrect: '#ff4d4f' };
  return (
    <div style={{
      border: `1px solid ${pass ? '#b7eb8f' : '#ffccc7'}`,
      background: pass ? '#f6ffed' : '#fff2f0',
      borderRadius: 10, padding: '14px 16px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{
          fontSize: 13, fontWeight: 700,
          color: pass ? '#389e0d' : '#cf1322',
          background: pass ? '#d9f7be' : '#ffccc7',
          padding: '2px 10px', borderRadius: 12,
        }}>
          {pass ? '✓ AI: Pass' : '✗ AI: Revise'}
        </span>
        <span style={{ fontSize: 13, color: '#555' }}>Score: <strong>{score}%</strong></span>
      </div>
      {feedback?.summary && (
        <p style={{ fontSize: 13, color: '#444', margin: '0 0 10px', lineHeight: 1.6 }}>
          {feedback.summary}
        </p>
      )}
      {feedback?.feedback?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {feedback.feedback.map((f, i) => (
            <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 700, color: VERDICT_COLOR[f.verdict] || '#888', flexShrink: 0, minWidth: 24 }}>
                Q{f.question_index + 1}
              </span>
              <span style={{ color: '#555' }}>{f.comment}</span>
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: 11, color: '#aaa', margin: '10px 0 0' }}>
        AI-graded · Your tutor will make the final decision
      </p>
    </div>
  );
}

// ── Helper: renders a question — its text and/or an uploaded image ────────────
function QuestionBody({ text, imageUrl, idx }) {
  return (
    <div style={{ fontSize: 13, flex: 1, minWidth: 0, color: '#0a0a0a' }}>
      {text && <MathRenderer content={text} />}
      {imageUrl && (
        <a href={imageUrl} target="_blank" rel="noreferrer">
          <img src={imageUrl} alt={`Question ${(idx ?? 0) + 1}`}
            style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 6, marginTop: text ? 8 : 0, objectFit: 'contain', display: 'block' }} />
        </a>
      )}
    </div>
  );
}

// ── Helper: renders student answer (per-question JSON or plain text) ──────────
function StudentAnswerDisplay({ answer }) {
  if (!answer) return null;
  try {
    const parsed = JSON.parse(answer);
    if (Array.isArray(parsed)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {parsed.map((qa, i) => (
            <div key={qa.question_id ?? i} style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a', marginBottom: 6 }}>
                {i + 1}. <MathRenderer content={qa.question_text} />
              </div>
              <div style={{ fontSize: 13, color: '#555', whiteSpace: 'pre-wrap', marginBottom: qa.image_url ? 8 : 0 }}>
                {qa.answer || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No answer provided</span>}
              </div>
              {qa.image_url && (
                <a href={qa.image_url} target="_blank" rel="noreferrer">
                  <img src={qa.image_url} alt="answer"
                    style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, border: '1px solid #ffd591', objectFit: 'contain' }} />
                </a>
              )}
            </div>
          ))}
        </div>
      );
    }
  } catch {}
  // Plain text fallback
  return (
    <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 12, fontSize: 13 }}>
      {answer}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function StudentView({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [selIdx, setSelIdx]           = useState(0);
  const [progress, setProgress]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [progLoading, setProgLoading] = useState(false);
  const [questionAnswers, setQuestionAnswers] = useState({}); // { [question_id]: answer }
  const [plainAnswer, setPlainAnswer]   = useState('');
  const [imageUrl, setImageUrl]         = useState('');       // fallback for no-question stages or photo mode
  const [uploading, setUploading]     = useState(false);
  const [submissionMode, setSubmissionMode] = useState({}); // { [progress_id]: 'type' | 'photo' }
  const [submitting, setSubmitting]   = useState(false);
  const [activeKey, setActiveKey]     = useState(null);
  const { message } = App.useApp();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/gate/assignments/student/${user.userId}`);
        setAssignments(res.data);
        if (res.data.length > 0) loadProgress(res.data[0]);
      } catch { message.error('Failed to load assignments'); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadProgress = async (assignment) => {
    setProgLoading(true);
    try {
      const res = await api.get(`/api/gate/assignments/${assignment.assignment_id}/progress`);
      setProgress(res.data);
      const activeStage = res.data.find(p => p.stage_status === 'active' || p.stage_status === 'submitted');
      setActiveKey(activeStage?.progress_id ?? null);
    } catch {}
    finally { setProgLoading(false); }
  };

  const switchAssignment = (idx) => {
    setSelIdx(idx);
    setProgress([]);
    setQuestionAnswers({});
    setPlainAnswer('');
    setImageUrl('');
    setSubmissionMode({});
    loadProgress(assignments[idx]);
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const urlRes = await api.get('/api/gate/upload-url', {
        params: { fileName: file.name, fileType: file.type },
      });
      await fetch(urlRes.data.uploadURL, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setImageUrl(urlRes.data.uploadURL.split('?')[0]);
      message.success('Image uploaded');
    } catch { message.error('Upload failed'); }
    finally { setUploading(false); }
    return false;
  };

  const handleSubmit = async (progressId, questions) => {
    setSubmitting(true);
    try {
      let studentAnswer;
      if (questions?.length > 0) {
        // Serialize per-question answers as JSON
        const answersArr = questions.map(q => ({
          question_id:   q.question_id,
          question_text: q.question_text,
          answer:        (questionAnswers[q.question_id] || '').trim(),
          image_url:     null,
        }));
        studentAnswer = JSON.stringify(answersArr);
      } else {
        studentAnswer = plainAnswer;
      }
      // Find this stage's data for AI grading context
      const stageData = progress.find(p => p.progress_id === progressId);
      await api.patch(`/api/gate/progress/${progressId}/submit`, {
        student_answer:   studentAnswer,
        answer_image_url: imageUrl || null,
        questions:        questions?.map(q => ({
          question_text: q.question_text,
          answer:        questionAnswers[q.question_id] || '',
          image_url:     null,
        })),
        gate_description: stageData?.gate_description || '',
        subject:          assignments[selIdx]?.subject_display || '',
      });
      message.success('Submitted! AI is grading your answers…');
      setQuestionAnswers({});
      setPlainAnswer('');
      setImageUrl('');
      loadProgress(assignments[selIdx]);
    } catch (e) { message.error(e.message); }
    finally { setSubmitting(false); }
  };

  const cur = assignments[selIdx];
  const readiness = calcReadiness(progress);
  const cleared = progress.filter(p => p.stage_status === 'completed').length;
  const total   = progress.filter(p => p.stage_status !== 'skipped').length;
  const daysLeft = cur?.exam_date ? dayjs(cur.exam_date).diff(dayjs(), 'day') : null;

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

  if (assignments.length === 0) {
    return <Empty description="No Roadmap assignments yet. Your tutor will assign one when ready." style={{ padding: 80 }} />;
  }

  return (
    <div>
      {assignments.length > 1 && (
        <Select style={{ width: 320, marginBottom: 20 }}
          value={selIdx} onChange={switchAssignment}
          options={assignments.map((a, i) => ({ value: i, label: `${a.template_title} · ${a.tutor_name}` }))}
        />
      )}

      {cur && (
        <>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Progress type="circle" percent={readiness} size={72} strokeColor="#0a0a0a" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{cur.template_title}</div>
                <div style={{ fontSize: 13, color: '#000' }}><span style={{ fontWeight: 600 }}>Tutor:</span> {cur.tutor_name}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0a0a0a' }}>{cleared}/{total}</div>
                <div style={{ fontSize: 11, color: '#888' }}>Gates cleared</div>
              </div>
              {daysLeft !== null && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: daysLeft < 14 ? '#fa541c' : '#0a0a0a' }}>{daysLeft}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>Days to exam</div>
                </div>
              )}
            </div>
          </div>

          {/* Pipeline */}
          <div style={{ marginBottom: 20 }}>
            <StagePipeline
              stages={progress}
              selected={progress.find(p => p.progress_id === activeKey) ?? null}
              onSelect={s => setActiveKey(s.progress_id === activeKey ? null : s.progress_id)}
              clickable
            />
          </div>

          {/* Stage list */}
          {progLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : (
              <Collapse
                accordion
                activeKey={activeKey}
                onChange={key => setActiveKey(key ?? null)}
                items={progress.map((p, i) => {
                  const isActive    = p.stage_status === 'active';
                  const isSubmitted = p.stage_status === 'submitted';
                  const isCompleted = p.stage_status === 'completed';
                  const isLocked    = p.stage_status === 'locked';
                  const isSkipped   = p.stage_status === 'skipped';

                  let headerTag = null;
                  if (isCompleted) headerTag = <Tag color="success">Completed</Tag>;
                  else if (isSubmitted) headerTag = <Tag color="orange">Awaiting review</Tag>;
                  else if (isActive) headerTag = <Tag color="processing">Active</Tag>;
                  else if (isSkipped) headerTag = <Tag>Skipped</Tag>;
                  else headerTag = <LockOutlined style={{ color: '#ccc' }} />;

                  return {
                    key: p.progress_id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: isCompleted ? '#52c41a' : isActive ? '#0a0a0a' : isSubmitted ? '#fa8c16' : '#e8e8e8',
                          color: isCompleted || isActive || isSubmitted ? '#fff' : '#bbb',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>
                          {isCompleted ? <CheckCircleOutlined /> : isLocked ? <LockOutlined style={{ fontSize: 10 }} /> : i + 1}
                        </div>
                        <span style={{
                          fontWeight: 500, fontSize: 13,
                          color: isSkipped ? '#bbb' : isLocked ? '#999' : '#0a0a0a',
                          textDecoration: isSkipped ? 'line-through' : 'none',
                        }}>
                          {p.stage_name}
                        </span>
                        <span style={{ marginLeft: 'auto' }}>{headerTag}</span>
                      </div>
                    ),
                    children: (
                      <div>
                        {isSkipped && (
                          <p style={{ fontSize: 13, color: '#bbb', fontStyle: 'italic', marginBottom: 0 }}>
                            This stage was skipped by your tutor.
                          </p>
                        )}
                        {!isSkipped && (
                          <>
                            {p.description && <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>{p.description}</p>}
                            {p.gate_description && (
                              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#444' }}>
                                <span style={{ fontWeight: 600 }}>Gate description: </span><MathRenderer content={p.gate_description} />
                              </div>
                            )}

                            {isLocked && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#aaa', fontSize: 13 }}>
                                <LockOutlined /> Complete the previous stage to unlock this one.
                              </div>
                            )}
                          </>
                        )}

                        {isActive && (
                          <>
                            {p.tutor_feedback && (
                              <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: 12, fontSize: 13, marginBottom: 12 }}>
                                <span style={{ fontWeight: 600 }}>Tutor feedback: </span>{p.tutor_feedback}
                              </div>
                            )}

                            {/* ── Submission mode selector (stages with questions only) ── */}
                            {p.questions?.length > 0 && !submissionMode[p.progress_id] && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>How will you submit your answers?</div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                  {[
                                    { key: 'type',  icon: '⌨️', title: 'Type my answers',  desc: 'Answer each question in the text box' },
                                    { key: 'photo', icon: '📷', title: 'Submit one photo',  desc: 'Upload a single photo of your handwritten work' },
                                  ].map(opt => (
                                    <div key={opt.key}
                                      onClick={() => setSubmissionMode(prev => ({ ...prev, [p.progress_id]: opt.key }))}
                                      style={{
                                        flex: 1, border: '2px solid #e8e8e8', borderRadius: 10,
                                        padding: '16px', cursor: 'pointer', textAlign: 'center',
                                      }}
                                    >
                                      <div style={{ fontSize: 24, marginBottom: 8 }}>{opt.icon}</div>
                                      <div style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a' }}>{opt.title}</div>
                                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{opt.desc}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ── Type mode: per-question text areas ── */}
                            {p.questions?.length > 0 && submissionMode[p.progress_id] === 'type' && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                  <span style={{ fontSize: 12, color: '#888' }}>Answer each question below</span>
                                  <Button type="link" size="small" style={{ padding: 0, fontSize: 12, height: 'auto' }}
                                    onClick={() => setSubmissionMode(prev => { const n = { ...prev }; delete n[p.progress_id]; return n; })}>
                                    Change method
                                  </Button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                                  {p.questions.map((q, qi) => (
                                    <div key={q.question_id} style={{
                                      border: '1px solid #e8e8e8', borderRadius: 10,
                                      overflow: 'hidden', background: '#fff',
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                    }}>
                                      <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <span style={{ fontWeight: 700, fontSize: 13, color: '#0a0a0a', flexShrink: 0 }}>{qi + 1}.</span>
                                        <QuestionBody text={q.question_text} imageUrl={q.image_url} idx={qi} />
                                      </div>
                                      <Input.TextArea
                                        autoSize={{ minRows: 3 }}
                                        placeholder="Your answer…"
                                        value={questionAnswers[q.question_id] || ''}
                                        onChange={e => setQuestionAnswers(prev => ({ ...prev, [q.question_id]: e.target.value }))}
                                        style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}

                            {/* ── Photo mode: show questions as reference + single upload ── */}
                            {p.questions?.length > 0 && submissionMode[p.progress_id] === 'photo' && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                  <span style={{ fontSize: 12, color: '#888' }}>Questions for reference — upload one photo of all your answers</span>
                                  <Button type="link" size="small" style={{ padding: 0, fontSize: 12, height: 'auto' }}
                                    onClick={() => setSubmissionMode(prev => { const n = { ...prev }; delete n[p.progress_id]; return n; })}>
                                    Change method
                                  </Button>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                  {p.questions.map((q, qi) => (
                                    <div key={q.question_id} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 6, marginBottom: 6, display: 'flex', gap: 8 }}>
                                      <span style={{ fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{qi + 1}.</span>
                                      <QuestionBody text={q.question_text} imageUrl={q.image_url} idx={qi} />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}

                            {/* ── No questions: plain textarea ── */}
                            {!p.questions?.length && (
                              <Input.TextArea
                                autoSize={{ minRows: 4 }}
                                placeholder="Type your answer here…"
                                value={plainAnswer}
                                onChange={e => setPlainAnswer(e.target.value)}
                                style={{ marginBottom: 10 }}
                              />
                            )}

                            {/* ── Image upload: no-question stages OR photo mode ── */}
                            {(!p.questions?.length || submissionMode[p.progress_id] === 'photo') && (
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                                <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
                                  <Button icon={<UploadOutlined />} loading={uploading} size="small">Upload image</Button>
                                </Upload>
                                {imageUrl && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <a href={imageUrl} target="_blank" rel="noreferrer">
                                      <img src={imageUrl} alt="upload"
                                        style={{ height: 40, borderRadius: 4, border: '1px solid #e8e8e8', objectFit: 'cover' }} />
                                    </a>
                                    <Button type="text" size="small" danger onClick={() => setImageUrl('')}>Remove</Button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── Submit button ── */}
                            {(!p.questions?.length || submissionMode[p.progress_id]) && (
                              <Button type="primary" loading={submitting}
                                style={{ background: '#0a0a0a', border: 'none' }}
                                onClick={() => handleSubmit(
                                  p.progress_id,
                                  submissionMode[p.progress_id] === 'photo' ? null : p.questions
                                )}>
                                Submit for review
                              </Button>
                            )}
                          </>
                        )}

                        {isSubmitted && (
                          <div>
                            <div style={{ fontSize: 13, color: '#fa8c16', marginBottom: 12 }}>
                              <ClockCircleOutlined /> Submitted — awaiting tutor review
                            </div>
                            <StudentAnswerDisplay answer={p.student_answer} />
                          </div>
                        )}

                        {isCompleted && (
                          <div>
                            <div style={{ fontSize: 13, color: '#52c41a', marginBottom: 12 }}>
                              <CheckCircleOutlined /> Stage cleared
                            </div>
                            {/* Show submitted answers for review */}
                            {p.student_answer && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your answers</div>
                                <StudentAnswerDisplay answer={p.student_answer} />
                              </div>
                            )}
                            {p.tutor_notes && (
                              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, fontSize: 13 }}>
                                <span style={{ fontWeight: 600 }}>Tutor notes: </span>{p.tutor_notes}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  };
                })}
              />
            )
          }
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function StageGate() {
  const { user } = useUser();

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Roadmap</h1>
        <p>{user.role === 'tutor'
          ? 'Track your students\' progress through each subject\'s learning stages.'
          : 'Track your progress and submit gate answers to advance through stages.'}
        </p>
      </div>
      {user.role === 'tutor' || user.role === 'admin'
        ? <TutorView user={user} />
        : <StudentView user={user} />}
    </AppLayout>
  );
}
